import React, { useState, useEffect } from 'react';
import { Trash2, Mail, MailOpen, Reply, Send, X, Check, Clock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessagesAdmin = () => {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contact/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const sorted = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setMessages(sorted);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const markAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/contact/messages/${messageId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, read: true } : msg
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    setDeleting(messageId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contact/messages/${messageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        toast({ title: 'Message deleted' });
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toast({ title: 'Error', description: 'Failed to delete message', variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  const openReplyModal = (msg) => {
    setReplyingTo(msg);
    setReplyText('');
  };

  const closeReplyModal = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  const sendReply = async () => {
    if (!replyText.trim()) {
      toast({ title: 'Error', description: 'Please enter a reply message', variant: 'destructive' });
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast({ title: 'Session expired', description: 'Please log in again', variant: 'destructive' });
        return;
      }
      
      const response = await fetch(`${API_URL}/api/contact/messages/${replyingTo.id}/reply`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reply: replyText })
      });

      // Handle 401 specifically
      if (response.status === 401) {
        toast({ title: 'Session expired', description: 'Please log in again', variant: 'destructive' });
        return;
      }

      const data = await response.json();
      
      if (response.ok) {
        toast({ title: 'Reply sent!', description: `Email sent to ${replyingTo.email}` });
        setMessages(prev => prev.map(msg => 
          msg.id === replyingTo.id 
            ? { ...msg, read: true, replied: true, replied_at: new Date().toISOString(), reply_text: replyText } 
            : msg
        ));
        closeReplyModal();
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to send reply', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast({ title: 'Error', description: 'Failed to send reply. Please try again.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading messages...</div>;

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Contact Messages</h2>
          {unreadCount > 0 && (
            <p className="text-sm text-pink-600 font-medium">{unreadCount} unread message{unreadCount !== 1 ? 's' : ''}</p>
          )}
        </div>
      </div>
      
      {messages.length === 0 ? (
        <p className="text-gray-600">No messages yet.</p>
      ) : (
        <div className="space-y-3">
          {messages.map((msg) => (
            <div 
              key={msg.id} 
              className={`border rounded-lg p-3 md:p-4 transition-all ${
                !msg.read ? 'bg-pink-50 border-pink-300 shadow-sm' : 'bg-white border-gray-200'
              }`}
              onClick={() => !msg.read && markAsRead(msg.id)}
            >
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!msg.read ? (
                      <Mail className="w-4 h-4 text-pink-600 flex-shrink-0" />
                    ) : (
                      <MailOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <p className={`${!msg.read ? 'font-bold text-gray-900' : 'font-medium text-gray-700'} truncate`}>
                      {msg.name}
                    </p>
                    <span className={`px-2 py-0.5 rounded text-xs flex-shrink-0 ${
                      msg.entry_type === 'vendor' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {msg.entry_type || 'customer'}
                    </span>
                    {msg.replied && (
                      <span className="px-2 py-0.5 rounded text-xs flex-shrink-0 bg-green-100 text-green-800 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Replied
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${!msg.read ? 'text-gray-700' : 'text-gray-500'} truncate`}>{msg.email}</p>
                  {msg.phone && <p className="text-sm text-gray-500">{msg.phone}</p>}
                </div>
                
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); openReplyModal(msg); }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Reply via email"
                  >
                    <Reply className="w-5 h-5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }}
                    disabled={deleting === msg.id}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete message"
                  >
                    <Trash2 className={`w-5 h-5 ${deleting === msg.id ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
              
              {msg.entry_type === 'vendor' && (
                <div className="bg-purple-50 p-2 rounded mt-2 text-sm space-y-1">
                  <p><strong>Company:</strong> {msg.company_name}</p>
                  <p><strong>Company Phone:</strong> {msg.company_phone}</p>
                  <p><strong>Website:</strong> {msg.website_url}</p>
                  <p><strong>Position:</strong> {msg.position}</p>
                  <p><strong>Best Contact:</strong> {msg.best_contact_method}</p>
                </div>
              )}
              
              <p className={`mt-2 ${!msg.read ? 'text-gray-800 font-medium' : 'text-gray-700'}`}>
                {msg.message}
              </p>

              {/* Show previous reply if exists */}
              {msg.replied && msg.reply_text && (
                <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700 text-sm font-medium mb-1">
                    <Check className="w-4 h-4" />
                    Your reply {msg.replied_at && (
                      <span className="text-green-600 font-normal">
                        ({new Date(msg.replied_at).toLocaleDateString()})
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-green-800 whitespace-pre-wrap">{msg.reply_text}</p>
                </div>
              )}
              
              <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-100">
                <div className="text-xs text-gray-500">
                  <span className="font-medium">
                    {new Date(msg.created_at).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </span>
                  <span className="mx-1">at</span>
                  <span>
                    {new Date(msg.created_at).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                    })}
                  </span>
                </div>
                {!msg.read && (
                  <span className="text-xs text-pink-600 font-medium">● New</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply Modal */}
      {replyingTo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-t-xl">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Reply className="w-5 h-5" />
                  Reply to {replyingTo.name}
                </h3>
                <p className="text-sm text-pink-100">{replyingTo.email}</p>
              </div>
              <button 
                onClick={closeReplyModal}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4">
              {/* Original message preview */}
              <div className="mb-4 bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1 font-medium">Original message:</p>
                <p className="text-sm text-gray-700 italic">"{replyingTo.message}"</p>
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(replyingTo.created_at).toLocaleString()}
                </p>
              </div>

              {/* Reply textarea */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your reply:
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your response here..."
                  className="w-full h-40 p-3 border-2 rounded-lg focus:border-pink-500 focus:outline-none resize-none"
                  autoFocus
                />
              </div>

              {/* Preview note */}
              <p className="text-xs text-gray-500 mb-4">
                This will send an email to <strong>{replyingTo.email}</strong> from your business email.
              </p>

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={closeReplyModal}
                  variant="outline"
                  className="flex-1"
                  disabled={sending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendReply}
                  className="flex-1 bg-pink-600 hover:bg-pink-700"
                  disabled={sending || !replyText.trim()}
                >
                  {sending ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesAdmin;
